import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Eye, Send, Loader2, Smartphone, Monitor, Pencil, RotateCcw } from "lucide-react";

/**
 * NotificationTemplatesPanel
 * ---------------------------
 * Admin panel for previewing and test-sending the 10 email templates KAHU sends
 * to users (adoption applicants, guardian donors, general donors, followups).
 * Backend endpoints:
 *  - GET  /api/admin/notification-templates
 *  - POST /api/admin/notification-templates/{id}/preview   (returns rendered HTML)
 *  - POST /api/admin/notification-templates/{id}/test-send (delivers real email)
 */
export default function NotificationTemplatesPanel({ defaultTestEmail = "" }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null); // id being loaded
  const [sending, setSending] = useState(null);
  const [preview, setPreview] = useState(null); // {template_name, subject, html, sample_recipient}
  const [previewWidth, setPreviewWidth] = useState("mobile"); // "mobile" (390) | "desktop" (640)
  const [testDialog, setTestDialog] = useState(null); // template being test-sent
  const [testEmail, setTestEmail] = useState(defaultTestEmail);
  const [editDialog, setEditDialog] = useState(null); // {template, slots[]}
  const [editValues, setEditValues] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editResetting, setEditResetting] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const EDITABLE_IDS = new Set([
    "application_received", "guardian_welcome", "donor_thanks",
    "application_status_sahiplenme_basladi", "application_status_sahiplendirildi", "application_status_reddedildi",
    "adoption_followup_30", "adoption_followup_90", "adoption_followup_180",
  ]);

  const openEditDialog = async (template) => {
    setLoadingSlots(true);
    setEditDialog({ template, slots: [] });
    setEditValues({});
    try {
      const { data } = await axios.get(`${API}/admin/notification-templates/${template.id}/slots`);
      const initial = {};
      (data.slots || []).forEach(s => { initial[s.key] = s.current; });
      setEditDialog({ template, slots: data.slots || [] });
      setEditValues(initial);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Alanlar yüklenemedi");
      setEditDialog(null);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSaveSlots = async () => {
    if (!editDialog) return;
    setEditSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/notification-templates/${editDialog.template.id}/slots`,
        { slots: editValues }
      );
      toast.success(`${data.saved} kaydedildi, ${data.cleared} varsayılana döndü`);
      setEditDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetSlots = async () => {
    if (!editDialog) return;
    if (!window.confirm(`"${editDialog.template.name}" şablonundaki tüm özel metinler silinecek ve varsayılana dönecek. Emin misin?`)) return;
    setEditResetting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/notification-templates/${editDialog.template.id}/slots/reset`
      );
      toast.success(`${data.cleared} özel metin silindi, varsayılana dönüldü`);
      setEditDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sıfırlama başarısız");
    } finally {
      setEditResetting(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/notification-templates`)
      .then(r => setTemplates(r.data?.templates || []))
      .catch(() => toast.error("Şablonlar yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const handlePreview = async (id) => {
    setPreviewing(id);
    try {
      const { data } = await axios.post(`${API}/admin/notification-templates/${id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setPreviewing(null);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Geçerli bir e-posta adresi girin");
      return;
    }
    setSending(testDialog.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/notification-templates/${testDialog.id}/test-send`,
        { to_email: testEmail }
      );
      if (data.status === "sent") {
        toast.success(`Test e-postası gönderildi (${data.sent_count} adet) → ${data.to_email}`);
        setTestDialog(null);
      } else {
        toast.error("Test e-postası gönderilemedi. Ayarları kontrol edin.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test gönderim başarısız");
    } finally {
      setSending(null);
    }
  };

  return (
    <Card className="bg-card border border-border/50" data-testid="notif-templates-panel">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-primary" strokeWidth={1.5} />
          <h3 className="font-heading text-lg">E-posta Şablonları</h3>
          <span className="ml-auto text-xs text-muted-foreground">
            {templates.length} şablon
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <div
                key={t.id}
                className="flex items-center gap-3 p-3 border border-border/40 rounded-lg hover:border-primary/30 transition-colors"
                data-testid={`template-row-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.description}</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">→ {t.recipient}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePreview(t.id)}
                  disabled={previewing === t.id}
                  className="flex-shrink-0"
                  data-testid={`template-preview-${t.id}`}
                >
                  {previewing === t.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Eye className="w-3.5 h-3.5 mr-1" />}
                  Önizle
                </Button>
                {EDITABLE_IDS.has(t.id) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(t)}
                    className="flex-shrink-0 border-primary/40 text-primary hover:bg-primary/5"
                    data-testid={`template-edit-${t.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setTestDialog(t); setTestEmail(defaultTestEmail); }}
                  className="flex-shrink-0"
                  data-testid={`template-test-${t.id}`}
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> Test gönder
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="template-preview-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <DialogTitle>{preview?.template_name} — Önizleme</DialogTitle>
                <DialogDescription>
                  Konu: <span className="font-medium text-foreground">{preview?.subject}</span>
                  <br />
                  <span className="text-xs">Örnek alıcı: {preview?.sample_recipient} · Sample verilerle render edildi</span>
                </DialogDescription>
              </div>
              {/* Viewport toggle — Pativista'nın Gmail iOS deneyimini görmesi için */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1 flex-shrink-0" data-testid="preview-viewport-toggle">
                <button
                  onClick={() => setPreviewWidth("mobile")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                    previewWidth === "mobile" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="preview-mobile-btn"
                  title="iPhone Gmail görünümü (390px)"
                >
                  <Smartphone className="w-3.5 h-3.5" /> Mobil
                </button>
                <button
                  onClick={() => setPreviewWidth("desktop")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                    previewWidth === "desktop" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="preview-desktop-btn"
                  title="Masaüstü Gmail görünümü (640px)"
                >
                  <Monitor className="w-3.5 h-3.5" /> Masaüstü
                </button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto border border-border/40 rounded-lg bg-slate-100 flex justify-center p-4">
            <iframe
              title="Email preview"
              srcDoc={preview?.html || ""}
              className="border-0 bg-white shadow-md transition-all duration-300 rounded"
              style={{
                width: previewWidth === "mobile" ? "390px" : "640px",
                height: "68vh",
                maxWidth: "100%",
              }}
              data-testid="template-preview-iframe"
              sandbox=""
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog — Text-Only Slot Editor */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editDialog?.template?.name} — İçerik Düzenle</DialogTitle>
            <DialogDescription>
              E-posta metinlerini düzenle. Tasarım (renk, düzen) korunur, sadece kelimeler değişir.
              <br/>
              <span className="text-xs">
                <code>{"{first_name}"}</code>, <code>{"{dog_name}"}</code> gibi köşeli parantezler dinamik değişkendir — dokunmadan bırak.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-4 py-2">
            {loadingSlots ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              (editDialog?.slots || []).map(s => (
                <div key={s.key} className="space-y-1" data-testid={`slot-${s.key}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-sm font-medium">{s.label}</Label>
                    {s.variables && s.variables.length > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        Değişkenler: {s.variables.map(v => <code key={v} className="mx-0.5 px-1 rounded bg-muted">{`{${v}}`}</code>)}
                      </span>
                    )}
                  </div>
                  {s.type === "textarea" ? (
                    <Textarea
                      rows={Math.min(6, Math.max(2, (editValues[s.key] || "").split("\n").length + 1))}
                      value={editValues[s.key] || ""}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [s.key]: e.target.value }))}
                      className="text-sm font-mono"
                      placeholder={s.default}
                      data-testid={`slot-input-${s.key}`}
                    />
                  ) : (
                    <Input
                      value={editValues[s.key] || ""}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [s.key]: e.target.value }))}
                      className="text-sm"
                      placeholder={s.default}
                      data-testid={`slot-input-${s.key}`}
                    />
                  )}
                  <p className="text-[10px] text-muted-foreground truncate">
                    Varsayılan: <span className="font-mono">{s.default}</span>
                  </p>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 pt-3 border-t border-border/40 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleResetSlots}
              disabled={editResetting || editSaving}
              className="flex-shrink-0 text-destructive border-destructive/30 hover:bg-destructive/5"
              data-testid="slot-reset-btn"
            >
              {editResetting
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sıfırlanıyor</>
                : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Varsayılana Sıfırla</>}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setEditDialog(null)} disabled={editSaving || editResetting}>
              İptal
            </Button>
            <Button onClick={handleSaveSlots} disabled={editSaving || editResetting} data-testid="slot-save-btn">
              {editSaving
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Kaydediliyor</>
                : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog open={!!testDialog} onOpenChange={(o) => !o && setTestDialog(null)}>
        <DialogContent data-testid="template-test-dialog">
          <DialogHeader>
            <DialogTitle>Test Gönderim — {testDialog?.name}</DialogTitle>
            <DialogDescription>
              Bu şablon örnek verilerle render edilip aşağıdaki adrese gönderilir.
              Gerçek kullanıcılara ulaşmayı önlemek için kendi test adresinizi kullanın.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label htmlFor="test-email">Hedef e-posta</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@pativista.org"
                className="mt-1.5"
                data-testid="template-test-email-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => setTestDialog(null)}
                className="flex-1"
                disabled={sending === testDialog?.id}
              >
                İptal
              </Button>
              <Button
                onClick={handleTestSend}
                disabled={sending === testDialog?.id || !testEmail.includes("@")}
                className="flex-1"
                data-testid="template-test-confirm-btn"
              >
                {sending === testDialog?.id
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gönderiliyor...</>
                  : <><Send className="w-4 h-4 mr-2" /> Gönder</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
